/**
 * ADR-0033: paying with Stripe Checkout, on the web and in the phone app. On the web the page
 * goes to Checkout and comes back with ?checkout=success. In the app Checkout opens in the
 * in-app browser; a page on the server hands back to the app (omni://open/…?checkout=success).
 * Either way the plan turns on when Stripe confirms the payment (the webhook), so the screen
 * checks again for a little while.
 */
import { isNative, openExternal } from "./platform";

/** Tells the server where checkout should come back to. */
export const checkoutClient: "web" | "app" = isNative ? "app" : "web";

export async function goToCheckout(url: string): Promise<void> {
  if (isNative) await openExternal(url);
  else window.location.href = url;
}

/** The in-app browser is left open after it hands back to the app: close it. */
export async function closeCheckout(): Promise<void> {
  if (!isNative) return;
  const { Browser } = await import("@capacitor/browser");
  await Browser.close().catch(() => undefined);
}

/** Re-check `done` every 2 s for up to a minute, and whenever the app comes back to the front. */
export function waitForActivation(check: () => Promise<boolean>, onDone: () => void): () => void {
  let stopped = false;
  const started = Date.now();
  const tick = async () => {
    if (stopped) return;
    if (await check().catch(() => false)) {
      stopped = true;
      onDone();
      return;
    }
    if (Date.now() - started < 60_000) setTimeout(tick, 2000);
  };
  const onVisible = () => document.visibilityState === "visible" && void tick();
  document.addEventListener("visibilitychange", onVisible);
  void tick();
  return () => {
    stopped = true;
    document.removeEventListener("visibilitychange", onVisible);
  };
}
